// @ts-nocheck
import React, { useEffect, useState } from 'react';
import { Drawer, List, ListItem, ListItemText, ListItemIcon, useMediaQuery, Collapse, Typography, Box, Button, Tooltip } from '@mui/material';
import { NavLink, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import './App.css';
import Header from './components/Header'; // Import Header
import Dashboard from './pages/Dashboard';
import Reports from './pages/Reports';
import Status from './pages/Status';
import Schedule from './pages/Schedule';
import Software from './pages/Software';
import { ExpandMore, ExpandLess, Summarize, Equalizer, CalendarMonth, FindInPage, Loupe, ManageSearch, Straighten, LaptopChromebook, School } from '@mui/icons-material';
import HomeWorkIcon from '@mui/icons-material/HomeWork';
import NewInspection from './pages/Inspection/NewInspection';
import MeasureCanada from './pages/Inspection/MeasureCanada';
import LoginPage from './pages/LoginPage';
import { useTheme } from '@mui/material/styles';
import { onAuthStateChange, signOut as supabaseSignOut } from './supabase';
import { styled, Theme } from '@mui/system';
import InspectionReports from './pages/Inspection/InspectionReports';
import ResetPasswordPage from './pages/ResetPasswordPage';

const drawerWidth = 240;
const miniDrawerWidth = 65; // Width for mini drawer
const headerHeight = 64;
const TIMEOUT_LIMIT = 900000;

const Main = styled('main', { shouldForwardProp: (prop) => prop !== 'open' && prop !== 'miniVariant' })<{ open?: boolean; miniVariant?: boolean; }>(({ theme, open, miniVariant }) => ({
  flexGrow: 1,
  padding: theme.spacing(3),
  paddingTop: `calc(${theme.spacing(3)} + ${headerHeight}px)`,
  transition: theme.transitions.create('margin', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),

  marginLeft: open ? (miniVariant ? miniDrawerWidth : drawerWidth) : 0,

  [theme.breakpoints.up('sm')]: {
    marginLeft: open ? (miniVariant ? miniDrawerWidth : drawerWidth) : 0,
    width: `calc(100% - ${open ? (miniVariant ? miniDrawerWidth : drawerWidth) : 0}px)`,
    marginRight: 0,
  },
}));


const menuItems = [
  { path: '/dashboard', label: 'Dashboard', icon: <HomeWorkIcon /> },
  { path: '/reports', label: 'Reports', icon: <Summarize /> },
  { path: '/status', label: 'Status', icon: <Equalizer /> },
  { path: '/schedule', label: 'Schedule', icon: <CalendarMonth /> },
  {
    path: '#',
    label: 'Quarterly Inspection',
    icon: <FindInPage />,
    subItems: [
      { path: '/inspection/new', label: 'New Inspection', icon: <Loupe /> },
      { path: '/inspection/reports', label: 'Inspection Reports', icon: <ManageSearch /> },
      { path: '/inspection/measure-canada', label: 'Measure Canada', icon: <Straighten /> },
    ]
  },
  {
    path: '/software',
    label: 'Software',
    icon: <LaptopChromebook />,
  },
  {
    path: 'https://coda.io/d/GLS-Service-Desk_d8u6nAcgNTm/GLS-Service-Desk_sub1Cjrg#_luBBXC3L',
    label: 'Documentation',
    icon: <School />,
    isExternal: true,
  },
];

// Error boundary component
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('App Error:', error);
    console.error('Error Info:', errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          minHeight="100vh"
          p={3}
        >
          <Typography variant="h5" color="error" gutterBottom>
            Something went wrong
          </Typography>
          <Typography variant="body1" color="textSecondary">
            {this.state.error?.message || 'An unexpected error occurred'}
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={() => window.location.reload()}
            sx={{ mt: 2 }}
          >
            Reload Page
          </Button>
        </Box>
      );
    }

    return this.props.children;
  }
}

const App: React.FC = () => {

  const location = useLocation();
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));
  const [openSubmenu, setOpenSubmenu] = React.useState(false);
  const isInitialMount = React.useRef(true);

  // Initialize state from localStorage or default values
  const [open, setOpen] = React.useState(() => {
    const savedOpen = localStorage.getItem('navbarOpen');
    // If on small screen, default to closed regardless of saved state
    if (isSmall) return false;
    // Otherwise use saved state or default to open
    const initialOpen = savedOpen !== null ? JSON.parse(savedOpen) : true;

    return initialOpen;
  });

  const [miniVariant, setMiniVariant] = React.useState(() => {
    const savedMiniVariant = localStorage.getItem('navbarMiniVariant');
    const initialMiniVariant = savedMiniVariant !== null ? JSON.parse(savedMiniVariant) : false;

    return initialMiniVariant;
  });

  const appBarHeight = 64;
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  // Save navbar state to localStorage whenever it changes
  useEffect(() => {
    // Skip initial render and only save when values actually change
    const savedOpen = localStorage.getItem('navbarOpen');
    const savedMiniVariant = localStorage.getItem('navbarMiniVariant');

    const currentOpenValue = JSON.stringify(open);
    const currentMiniVariantValue = JSON.stringify(miniVariant);

    // Only save if values have changed and we're not on small screens
    if (!isSmall &&
      (savedOpen !== currentOpenValue || savedMiniVariant !== currentMiniVariantValue)) {
      localStorage.setItem('navbarOpen', currentOpenValue);
      localStorage.setItem('navbarMiniVariant', currentMiniVariantValue);

    }
  }, [open, miniVariant, isSmall]);

  const updateActivityTime = () => {
    window.localStorage.setItem('lastActivityTime', Date.now().toString());
  };

  const checkTimeout = () => {
    const lastActivityTime = window.localStorage.getItem('lastActivityTime');
    if (!lastActivityTime) updateActivityTime();
    const currentTime = Date.now();
    const timeElapsed = currentTime - parseInt(lastActivityTime);
    if (timeElapsed > TIMEOUT_LIMIT) {
      supabaseSignOut();
    }
  };

  useEffect(() => {
    try {
      const { subscription } = onAuthStateChange((user) => {

        setIsAuthenticated(!!user);
        setLoading(false);
        if (user) {
          updateActivityTime();
        }
      });

      const timeoutCheckInterval = setInterval(checkTimeout, 300000);

      // Add listeners for various user actions to reset the timeout
      window.addEventListener('mousemove', updateActivityTime);
      window.addEventListener('keydown', updateActivityTime);
      window.addEventListener('click', updateActivityTime);

      return () => {
        subscription?.unsubscribe();
        clearInterval(timeoutCheckInterval);
        window.removeEventListener('mousemove', updateActivityTime);
        window.removeEventListener('keydown', updateActivityTime);
        window.removeEventListener('click', updateActivityTime);
      };
    } catch (error) {
      console.error('Error in auth setup:', error);
      setLoading(false);
    }
  }, []);

  // Update drawer state when screen size changes
  useEffect(() => {
    // Skip the first render to avoid overriding localStorage values
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    if (isSmall) {
      setOpen(false);
      setMiniVariant(false);
    } else {
      // Only set default values if there's no saved state in localStorage
      const savedOpen = localStorage.getItem('navbarOpen');
      const savedMiniVariant = localStorage.getItem('navbarMiniVariant');

      if (savedOpen === null) {
        setOpen(true);
      }

      if (savedMiniVariant === null) {
        setMiniVariant(false);
      }
    }
  }, [isSmall]);

  const handleDrawerOpen = () => {
    if (miniVariant) {
      setMiniVariant(false);
    }
    setOpen(true);
  };

  const handleDrawerClose = () => {
    if (!isSmall) {
      // On larger screens, switch to mini variant instead of fully closing
      setMiniVariant(true);
    } else {
      // On small screens, fully close the drawer
      setOpen(false);
    }
  };

  const toggleSubmenu = () => {
    setOpenSubmenu(!openSubmenu);
  };

  const toggleMiniVariant = () => {
    setMiniVariant(!miniVariant);
  };

  // Check if current path is reset-password
  const isResetPasswordPage = location.pathname === '/reset-password';

  return (
    <ErrorBoundary>
      {loading ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="100vh"
        >
          <Typography variant="h6">Loading...</Typography>
        </Box>
      ) : (
        <>
          {/* Special case for reset password page */}
          {isResetPasswordPage ? (
            <ResetPasswordPage />
          ) : !isAuthenticated ? (
            <LoginPage />
          ) : (
            <>
              <Drawer
                anchor="left"
                open={open}
                onClose={isSmall ? handleDrawerClose : undefined}
                variant={isSmall ? 'temporary' : 'permanent'}
                PaperProps={{
                  style: {
                    width: miniVariant && !isSmall ? miniDrawerWidth : drawerWidth,
                    marginTop: appBarHeight,
                    overflowX: 'hidden',
                    transition: theme.transitions.create('width', {
                      easing: theme.transitions.easing.sharp,
                      duration: theme.transitions.duration.enteringScreen,
                    }),
                  }
                }}
                sx={{ zIndex: 1200 }}
              >
                <div style={{ flex: 1, overflow: 'auto' }}>
                  <List>
                    {menuItems.map((item) => {
                      if (item.isExternal) {
                        return (
                          <Tooltip key={item.path} title={item.label} placement="right" arrow disableHoverListener={!miniVariant || isSmall}>
                            <ListItem
                              button
                              component="a"
                              href={item.path}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{
                                justifyContent: miniVariant && !isSmall ? 'center' : 'flex-start',
                                px: miniVariant && !isSmall ? 1 : 2
                              }}
                            >
                              <ListItemIcon sx={{ minWidth: 0, mr: miniVariant && !isSmall ? 0 : 'auto', justifyContent: 'center' }}>
                                {item.icon}
                              </ListItemIcon>
                              {(!miniVariant || isSmall) && <ListItemText primary={item.label} />}
                            </ListItem>
                          </Tooltip>
                        );
                      } else if (item.subItems && item.subItems.length > 0) {
                        return (
                          <React.Fragment key={item.path}>
                            <Tooltip title={item.label} placement="right" arrow disableHoverListener={!miniVariant || isSmall}>
                              <ListItem
                                button
                                onClick={toggleSubmenu}
                                sx={{
                                  justifyContent: miniVariant && !isSmall ? 'center' : 'flex-start',
                                  px: miniVariant && !isSmall ? 1 : 2
                                }}
                              >
                                <ListItemIcon sx={{ minWidth: 0, mr: miniVariant && !isSmall ? 0 : 'auto', justifyContent: 'center' }}>
                                  {item.icon}
                                </ListItemIcon>
                                {(!miniVariant || isSmall) && <ListItemText primary={item.label} />}
                                {(!miniVariant || isSmall) && (openSubmenu ? <ExpandLess /> : <ExpandMore />)}
                              </ListItem>
                            </Tooltip>
                            <Collapse in={openSubmenu} timeout="auto" unmountOnExit>
                              <List component="div" disablePadding>
                                {item.subItems.map((subItem) => (
                                  <Tooltip key={subItem.path} title={subItem.label} placement="right" arrow disableHoverListener={!miniVariant || isSmall}>
                                    <ListItem
                                      button
                                      component={NavLink}
                                      to={subItem.path}
                                      sx={{
                                        paddingLeft: miniVariant && !isSmall ? '10px' : '20px',
                                        justifyContent: miniVariant && !isSmall ? 'center' : 'flex-start',
                                        px: miniVariant && !isSmall ? 1 : 2
                                      }}
                                    >
                                      <ListItemIcon sx={{ minWidth: 0, mr: miniVariant && !isSmall ? 0 : 'auto', justifyContent: 'center' }}>
                                        {subItem.icon}
                                      </ListItemIcon>
                                      {(!miniVariant || isSmall) && <ListItemText primary={subItem.label} />}
                                    </ListItem>
                                  </Tooltip>
                                ))}
                              </List>
                            </Collapse>
                          </React.Fragment>
                        );
                      } else {
                        return (
                          <Tooltip key={item.path} title={item.label} placement="right" arrow disableHoverListener={!miniVariant || isSmall}>
                            <ListItem
                              button
                              component={NavLink}
                              to={item.path}
                              sx={{
                                justifyContent: miniVariant && !isSmall ? 'center' : 'flex-start',
                                px: miniVariant && !isSmall ? 1 : 2
                              }}
                            >
                              <ListItemIcon sx={{ minWidth: 0, mr: miniVariant && !isSmall ? 0 : 'auto', justifyContent: 'center' }}>
                                {item.icon}
                              </ListItemIcon>
                              {(!miniVariant || isSmall) && <ListItemText primary={item.label} />}
                            </ListItem>
                          </Tooltip>
                        );
                      }
                    })}
                  </List>
                </div>
                <div style={{ marginTop: 'auto', padding: theme.spacing(2), marginBottom: '50px' }}>
                  {(!miniVariant || isSmall) && (
                    <Typography variant="body2" color="textSecondary" align="center">
                      V2.0.0
                    </Typography>
                  )}
                </div>
              </Drawer>

              <Main open={open} miniVariant={miniVariant}>
                <Header
                  menuItems={menuItems}
                  onOpenDrawer={handleDrawerOpen}
                  handleDrawerClose={handleDrawerClose}
                  open={open}
                  miniVariant={miniVariant}
                  toggleMiniVariant={toggleMiniVariant}
                />

                <Routes>
                  <Route path="/" element={<Navigate to="/dashboard" replace />} />
                  <Route
                    path="/dashboard"
                    element={<Dashboard />}
                  />
                  <Route path="/reports" element={<Reports />} />
                  <Route path="/status" element={<Status />} />
                  <Route path="/schedule" element={<Schedule />} />
                  <Route path="/inspection/new" element={<NewInspection />} />
                  <Route path="/inspection/reports" element={<InspectionReports />} />
                  <Route path="/inspection/measure-canada" element={<MeasureCanada />} />
                  <Route path="/software" element={<Software />} />
                  {/* Reset password is handled above */}
                  {/* ...add routes for other pages as needed */}
                </Routes>
              </Main>
            </>
          )}
        </>
      )}
    </ErrorBoundary>
  );
};

export default App;